import {Box, Flex, Spinner, Tab, TabList, TabPanel, TabPanels, Tabs, useColorMode, useColorModeValue, Icon, Text, Badge, HStack, VStack, Button, useDisclosure, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton} from "@chakra-ui/react";
import React, { Suspense, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import API, { getCompressedFile, cleanPicturePath } from "../../context/API";
import MenuOverview from "./MenuOverview";
import SideBox from "./SideBox";
import CustomizationsForOverview from "./Customisations/CustomizationsForOverview";
import { TAB_CONFIG } from "./Constant";
import useCommonToast from "../../utils/toast";
import { 
  MdRestaurant, 
  MdCategory, 
  MdInventory, 
  MdBlock, 
  MdTune, 
  MdSchedule,
  MdAdd,
  MdSearch,
  MdFilterList,
  MdViewModule,
  MdViewList,
  MdSort
} from "react-icons/md";
import { FaBars, FaCog } from "react-icons/fa";

const MotionBox = motion(Box);

const Menu = () => {
  const [editingItem, setEditingItem] = useState(null);
  const [isAddItemOpen, setIsAddItemOpen] = useState(false);
  const [customizationId, setCustomizationId] = useState("");
  const [option, setOption] = useState(null);
  const { colorMode } = useColorMode();
  const [imageFile, setImageFile] = useState(null);
  const imageInputRef = useRef(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedCustomizationId, setSelectedCustomizationId] = useState(null);
  const [dataNeedsRefresh, setDataNeedsRefresh] = useState(false);
  const [categoriesToRefetch, setCategoriesToRefetch] = useState([]);
  const [menuOverviewKey, setMenuOverviewKey] = useState(0);
  const [customizations, setCustomizations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [showFilters, setShowFilters] = useState(false);
  const { isOpen: isMobileMenuOpen, onOpen: onMobileMenuOpen, onClose: onMobileMenuClose } = useDisclosure();
  const [imageCache] = useState(new Map());
  const queryClient = useQueryClient();

  const {
    showItemUpdated,
    showItemSoldOut,
    showMissingFields,
    showItemCreated,
    showImageUploadError,
    showImageReplaceError,
    showApiError,
  } = useCommonToast();

  // Color mode values
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const subtleTextColor = useColorModeValue("gray.600", "gray.300");
  const accentColor = useColorModeValue("teal.500", "teal.400");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  const handleMenuOverviewRefresh = useCallback(() => {
    setMenuOverviewKey((prevKey) => prevKey + 1);
    // Also clear any pending category refetches since we're doing a full refresh
    setCategoriesToRefetch([]);
    // Invalidate cache for immediate refresh
    queryClient.invalidateQueries(["categories"]);
  }, [queryClient]);

  const handleCustomizationCreated = useCallback(() => {
    // Trigger refresh of customization data in SideBox
    setMenuOverviewKey((prevKey) => prevKey + 1);
    // Invalidate cache for immediate refresh
    queryClient.invalidateQueries(["categories"]);
  }, [queryClient]);

  const handleTabChange = (index) => {
    setTabIndex(index);
    if (index === 2 || index === 3) {
      setDataNeedsRefresh(true);
    }

    if (index === 0 && dataNeedsRefresh) {
      setDataNeedsRefresh(false);
      setMenuOverviewKey((prevKey) => prevKey + 1);
      // Invalidate cache for immediate refresh
      queryClient.invalidateQueries(["categories"]);
      handleCloseSideBox();
    }

    if (index !== 5) {
      setSelectedCustomizationId(null);
      // Reset customization data when switching away from Customizations tab
      setCustomizationId("");
      setOption(null);
    }
  };

  const handleCustomizationClick = (customizationId) => {
    setSelectedCustomizationId(customizationId);
    setTabIndex(5);
  };

  const handleAddItem = async () => {
    setEditingItem(null);
    setImageFile(null);
    setIsAddItemOpen(true);
  };

  const handleCloseSideBox = () => {
    setIsAddItemOpen(false);
    setEditingItem(null);
  };

  const handleDuplicate = async (affectedCategories) => {
    try {
      const apiResponse = await API.get(`/items/clone/${editingItem.id}`);
      showItemCreated();
      setEditingItem(apiResponse.data.data);
    } catch (error) {
      console.error("Error saving category:", error);
      showApiError(error, "Unable to save the item.");
    }
    setCategoriesToRefetch((prev) => [
      ...new Set([...prev, ...affectedCategories]),
    ]);
  };

  const handleImageUpload = async (file) => {
    const cacheKey = `${file.name}-${file.size}-${file.lastModified}`;
    
    if (imageCache.has(cacheKey)) {
      return imageCache.get(cacheKey);
    }
    
    const compressedFile = await getCompressedFile(file);
    const formData = new FormData();
    formData.append("file", compressedFile);
    formData.append("type", "kiwikiosk_items");
    
    try {
      const response = await API.post("/images/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const fullUrl = response.data.url;
      const relativePath = cleanPicturePath(fullUrl);
      
      // Cache the result
      imageCache.set(cacheKey, relativePath);
      return relativePath;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data || "Unable to upload the image.";
      showImageUploadError(errorMessage);
    }
  };

  const handleImageReplace = async (imageKey, file) => {
    const compressedFile = await getCompressedFile(file);
    const formData = new FormData();
    formData.append("file", compressedFile);
    formData.append("type", "kiwikiosk_items");

    try {
      const encodedImageKey = encodeURIComponent(imageKey);
      const response = await API.put(`/images/${encodedImageKey}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const fullUrl = response.data.url;
      const relativePath = cleanPicturePath(fullUrl);
      return relativePath;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data ||
        "Unable to replace the image.";
      showImageReplaceError(errorMessage);
      return null;
    }
  };

  const validateRequiredFields = (updatedItemData, isCreating) => {
    if (!isCreating) return true;

    const missingFields = [];
    if (!updatedItemData.item || updatedItemData.item.trim() === "") {
      missingFields.push("Item Name");
    }
    if (!updatedItemData.price || isNaN(updatedItemData.price)) {
      missingFields.push("Price");
    }
    if (
      !updatedItemData.category_names ||
      updatedItemData.category_names.length === 0
    ) {
      missingFields.push("Category");
    }

    if (missingFields.length > 0) {
      showMissingFields(missingFields);
      return false;
    }
    return true;
  };

  const processImage = async (updatedItemData, isCreating) => {
    let picturePath = updatedItemData.picture;
    if (imageFile) {
      if (editingItem) {
        const imageKey = cleanPicturePath(picturePath);
        picturePath = await handleImageReplace(imageKey, imageFile);
      } else {
        picturePath = await handleImageUpload(imageFile);
      }
    } else {
      if (isCreating && (!picturePath || picturePath.trim() === "")) {
        picturePath = "default.jpg";
      } else {
        picturePath = cleanPicturePath(picturePath);
      }
    }

    return picturePath;
  };

  const formatItemData = (updatedItemData, picturePath) => {
    const categoryNames = Array.isArray(updatedItemData.category_names) 
      ? updatedItemData.category_names 
      : [];
    
    return {
      ...updatedItemData,
      price: parseFloat(updatedItemData.price) || 0,
      picture: picturePath,
      sequence_number: updatedItemData.sequence_number,
      customizations: (updatedItemData.customizations || [])
        .filter((c) => c && c.id != null) // Filter out undefined/null customizations
        .map((c) => ({
          customization: c.id.toString(),
          sequence_number: c.sequence_number,
        })),
      category_names: categoryNames.map((category) => {
        if (
          typeof category === "object" &&
          category !== null &&
          "id" in category
        ) {
          return category.id.toString();
        }
        return category.toString();
      }),
    };
  };

  const performApiOperation = async (formattedItemData, isCreating) => {
    if (editingItem) {
      await API.put(`/items/${editingItem.id}`, formattedItemData);
      showItemUpdated();
    } else {
      delete formattedItemData.sequence_number;
      formattedItemData.name = formattedItemData.item;
      await API.post("/items", formattedItemData);
      showItemCreated();
    }
  };

  const updateUIState = (affectedCategories) => {
    setCategoriesToRefetch((prev) => [
      ...new Set([...prev, ...affectedCategories]),
    ]);
    handleCloseSideBox();
  };

  const handleSaveItem = async (updatedItemData, affectedCategories) => {
    setLoading(true);
    try {
      const isCreating = !editingItem;
      if (!validateRequiredFields(updatedItemData, isCreating)) {
        setLoading(false);
        return;
      }
      
      const picturePath = await processImage(updatedItemData, isCreating);
      const formattedItemData = formatItemData(updatedItemData, picturePath);
      
      // Perform the API operation
      await performApiOperation(formattedItemData, isCreating);
      
      // Force refresh of menu overview to show updated data
      setMenuOverviewKey((prevKey) => prevKey + 1);
      
      // Also invalidate React Query cache to ensure immediate refresh
      queryClient.invalidateQueries(["categories"]);
      
      updateUIState(affectedCategories);
      setLoading(false);
    } catch (error) {
      showApiError(error, "Unable to save the item.");
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleDeleteImage = () => {
    setImageFile(null);
    // Clear the file input
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
    // If editing existing item, set picture to default
    if (editingItem) {
      setEditingItem({ ...editingItem, picture: "default.jpg" });
    }
  };

  // Enhanced tab configuration with completely unique icons and descriptions
  const enhancedTabConfig = TAB_CONFIG.map((tab, index) => {
    const icons = [
      MdRestaurant,        // Overview - Restaurant icon
      MdViewModule,        // Menus - Grid view icon  
      MdCategory,          // Categories - Category icon
      MdInventory,         // Items - Inventory icon
      MdBlock,             // Sold out - Block/stop icon
      MdTune,              // Customizations - Tune/settings icon
      MdFilterList,        // Sold out customizations - Filter icon
      MdSort,              // Sold out options - Sort icon
      MdSchedule           // Schedules - Schedule icon
    ];
    
    const descriptions = [
      "Manage your menu overview and items",
      "Organize your menu categories", 
      "Manage individual menu items",
      "Handle sold out items",
      "Customize your menu options",
      "Set menu schedules and availability",
      "Manage sold out customizations",
      "Handle sold out options",
      "Set menu schedules and availability"
    ];
    
    return {
      ...tab,
      icon: icons[index] || MdRestaurant,
      description: descriptions[index] || "Menu management"
    };
  });

  const Loader = () => {
    return (
      <Box p={4}>
        <Flex justify="center" align="center" height="200px">
          <Spinner size="xl" color={accentColor} />
        </Flex>
      </Box>
    );
  };

  return (
    <>
      <Box w="full" bg={bgColor} minH="100vh">
        {/* Enhanced Tabs */}
        <Box px={{ base: 4, md: 6, lg: 8 }} py={4}>
          <Tabs
            colorScheme="blue"
            onChange={handleTabChange}
            index={tabIndex}
            aria-label="Menu management tabs"
            variant="enclosed"
            size="lg"
          >
            <TabList
              bg={bgColor}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
              p={2}
              overflowX="auto"
              overflowY="hidden"
              whiteSpace="nowrap"
              sx={{
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                "&::-webkit-scrollbar-track": {
                  display: "none",
                },
                "&::-webkit-scrollbar-thumb": {
                  display: "none",
                },
                scrollbarWidth: "none", // Firefox
                msOverflowStyle: "none", // IE and Edge
              }}
            >
              {enhancedTabConfig.map((tab) => (
                <Tab
                  key={tab.id}
                  fontSize={{ base: "sm", md: "md" }}
                  px={{ base: 4, md: 6 }}
                  py={3}
                  role="tab"
                  aria-selected={tabIndex === tab.id}
                  _selected={{
                    bg: "blue.50",
                    color: "blue.700",
                    borderColor: "blue.200",
                    fontWeight: "600",
                  }}
                  _hover={{
                    bg: "teal.50",
                    color: "teal.700",
                    borderColor: "teal.200",
                  }}
                  borderRadius="lg"
                  transition="all 0.2s ease-in-out"
                >
                  <HStack spacing={2}>
                    <Icon as={tab.icon} boxSize={5} />
                    <Text>{tab.label}</Text>
                  </HStack>
                </Tab>
              ))}
            </TabList>

            <TabPanels mt={6}>
              <TabPanel role="tabpanel" aria-label="Menu overview" p={0}>
                <Flex direction={{ base: "column", lg: "row" }} gap={6}>
                  <MotionBox
                    key={menuOverviewKey}
                    flex={1}
                    initial={{ width: "100%" }}
                    animate={{
                      width:
                        isAddItemOpen || editingItem
                          ? { base: "100%", lg: "calc(100% - 400px)" }
                          : "100%",
                    }}
                    transition={{ type: "tween", duration: 0.5 }}
                  >
                    <MenuOverview
                      refreshTrigger={menuOverviewKey}
                      setEditingItem={setEditingItem}
                      setImageFile={setImageFile}
                      setIsAddItemOpen={setIsAddItemOpen}
                      handleAddItem={handleAddItem}
                      categoriesToRefetch={categoriesToRefetch}
                      onCategoriesRefetched={() => {
                        setCategoriesToRefetch([]);
                      }}
                      onCustomizationClick={(id, option) => {
                        handleCloseSideBox();
                        setSelectedCustomizationId(id);
                        if (option) {
                          setOption(option);
                        } else {
                          setOption(null);
                        }
                        // Switch to Customizations tab when clicking on a customization
                        setTabIndex(5);
                      }}
                      setCustomizations={setCustomizations}
                      setCategories={setCategories}
                      viewMode={viewMode}
                      showFilters={showFilters}
                    />
                  </MotionBox>

                  {/* Removed CustomizationsForOverview from Overview tab - now handled by dedicated Customizations tab */}

                  {(isAddItemOpen || editingItem) && (
                    <motion.div
                      initial={{ x: "100%", opacity: 0 }}
                      animate={{
                        x: isAddItemOpen || editingItem ? 0 : "100%",
                        opacity: isAddItemOpen || editingItem ? 1 : 0,
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 80,
                        duration: 0.5,
                      }}
                      style={{
                        width: { base: "100%", lg: "400px" },
                        position: "relative",
                      }}
                    >
                      <SideBox
                        key={editingItem?.id || 'new-item'}
                        isOpen={isAddItemOpen || editingItem}
                        onClose={handleCloseSideBox}
                        item={editingItem}
                        onSave={handleSaveItem}
                        imageFile={imageFile}
                        imageInputRef={imageInputRef}
                        handleImageChange={handleImageChange}
                        handleDeleteImage={handleDeleteImage}
                        onCustomizationClick={handleCustomizationClick}
                        handleDuplicate={handleDuplicate}
                        customizations={customizations}
                        setCustomizations={setCustomizations}
                        categories={categories}
                        setCategories={setCategories}
                        loading={loading}
                        onCustomizationCreated={handleCustomizationCreated}
                      />
                    </motion.div>
                  )}
                </Flex>
              </TabPanel>

              {enhancedTabConfig.slice(1).map((tab) => {
                const Component = tab.component;
                return (
                  <TabPanel key={tab.id} role="tabpanel" aria-label={tab.label} p={0}>
                    <Box 
                      bg={bgColor} 
                      border="1px solid" 
                      borderColor={borderColor} 
                      borderRadius="xl" 
                      p={6}
                      shadow="sm"
                    >
                      <VStack align="flex-start" spacing={4} mb={6}>
                        <HStack spacing={3}>
                          <Box
                            p={2}
                            borderRadius="lg"
                            bg={`${accentColor}20`}
                            color={accentColor}
                          >
                            <Icon as={tab.icon} boxSize={6} />
                          </Box>
                          <VStack align="flex-start" spacing={1}>
                            <Text fontSize="xl" fontWeight="600" color={textColor}>
                              {tab.label}
                            </Text>
                            <Text fontSize="sm" color={subtleTextColor}>
                              {tab.description}
                            </Text>
                          </VStack>
                        </HStack>
                      </VStack>
                      
                      <Suspense fallback={<Loader />}>
                        <Component
                          tabIndex={tabIndex}
                          {...(tab.hasRefresh && {
                            onCategoryChanged: handleMenuOverviewRefresh,
                          })}
                          {...(tab.hasCustomization && {
                            selectedCustomizationId: selectedCustomizationId,
                          })}
                        />
                      </Suspense>
                    </Box>
                  </TabPanel>
                );
              })}
            </TabPanels>
          </Tabs>
        </Box>

        {/* Mobile Drawer */}
        <Drawer isOpen={isMobileMenuOpen} placement="right" onClose={onMobileMenuClose} size="xs">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px" borderColor={borderColor}>
              Menu Options
            </DrawerHeader>
            <DrawerBody pt={4}>
              <VStack spacing={4} align="stretch">
                {enhancedTabConfig.map((tab) => (
                  <Button
                    key={tab.id}
                    variant={tabIndex === tab.id ? "solid" : "ghost"}
                    colorScheme={tabIndex === tab.id ? "blue" : "gray"}
                    justifyContent="flex-start"
                    leftIcon={<Icon as={tab.icon} />}
                    onClick={() => {
                      handleTabChange(tab.id);
                      onMobileMenuClose();
                    }}
                    borderRadius="lg"
                  >
                    {tab.label}
                  </Button>
                ))}
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Box>
    </>
  );
};

export default Menu;
